import React, { useState, useEffect, useCallback } from "react";
import { Button, Form } from 'react-bootstrap';
import ClosingProgressPopup from "./closingProgressPopup";
import { ConfirmationActionPopup } from './confirmationActionPopup';
import { ReactComponent as ArrowLeft } from '../img/arrow_left.svg';
import { db, database } from '../firebase';
import { ref, get, update, serverTimestamp, remove } from 'firebase/database';
import { addDoc, collection } from 'firebase/firestore';

const kam_eye_issues = collection(db, 'kam_eye');

export const HandleIssue = ({ issue, issueGroup, onClose }) => {
    const userName = sessionStorage.getItem('userName');
    const userLastName = sessionStorage.getItem('userLastName');
    const isMorethanOne = issueGroup?.length > 1;
    const kamEyePath = '/kam_eye';
    const [editedIssue, setEditedIssue] = useState(null);
    const [showConfirmationActionPopup, setShowConfirmationActionPopup] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [takeActionToAll, setTakeActionToAll] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [showProgressPopup, setShowProgressPopup] = useState(false);
    const [currentIssueIndex, setCurrentIssueIndex] = useState(0);

    function formatTimestamp(timestamp) {
        if (!timestamp) {
            return '-';
        }

        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB');
    }

    const closeEditIssue = useCallback(() => {
        onClose();
        setEditedIssue(null);
    }, [onClose]);

    const saveEditedIssue = useCallback(async (currentIssue) => {
        if (!editedIssue) {
            setEditedIssue(null);
            closeEditIssue();
            return;
        }
    
        try {
            const issuePath = `${kamEyePath}/${currentIssue.id}`;
            const issueRef = ref(database, issuePath);
            let updatedIssueData = { ...editedIssue };
    
            // Handle the new message
            if (newMessage.trim() !== '') {
                let messageKey;
                let messageNumber = 1;
    
                // Osiris no longer writes a `timestamps` object at all (see
                // createdAt) - it may be entirely absent on a fresh issue.
                if (!updatedIssueData.timestamps) {
                    updatedIssueData.timestamps = {};
                }
                if (!updatedIssueData.timestamps.messages) {
                    updatedIssueData.timestamps.messages = {};
                }
    
                messageKey = `${userName}_${userLastName}_${messageNumber}`;
                while (updatedIssueData.timestamps.messages[messageKey]) {
                    messageNumber++;
                    messageKey = `${userName}_${userLastName}_${messageNumber}`;
                }
    
                updatedIssueData.timestamps.messages[messageKey] = {
                    message: newMessage,
                    timestamp: serverTimestamp(),
                };
            }
    
            // Save the updated issue data to Firestore
            await update(issueRef, updatedIssueData);
    
            // Clear state and close the edit issue modal
            setNewMessage('');
            setEditedIssue(null);
            closeEditIssue();
        } catch (error) {
            console.error('Error updating the issue:', error);
        }
    }, [editedIssue, newMessage, userName, userLastName, closeEditIssue]);

    const saveEditedIssueOrIssues = useCallback(async () => {
        if (editedIssue) {
            if (isMorethanOne && takeActionToAll) {
                try {
                    // Iterate through each issue in the group and prepare the updates
                    issueGroup.forEach((groupIssue) => {
                        saveEditedIssue(groupIssue);
                    });
                }
                catch (error) {
                    console.error('Error updating the issue:', error);
                }
            } else {
                saveEditedIssue(editedIssue);
            }
        }
    }, [editedIssue, isMorethanOne, takeActionToAll, issueGroup, saveEditedIssue]);

    const closeIssue = useCallback(async (issueId, index, length) => {
        try {
            const issuePath = `${kamEyePath}/${issueId}`;
            const issueRef = ref(database, issuePath);
            const issueSnapshot = await get(issueRef);
            const issueData = issueSnapshot.val();

            let closedTimestamp;
            let uniqueKey = 'Closed';
            let num = 1;

            if (!issueData.timestamps) {
                issueData.timestamps = {};
            }
            if (!issueData.timestamps.close) {
                issueData.timestamps.close = {};
            }

            while (issueData.timestamps.close[uniqueKey]) {
                num++;
                uniqueKey = `Closed_${num}`;
            }
            closedTimestamp = serverTimestamp();

            issueData.timestamps.close[uniqueKey] = closedTimestamp;
            await addDoc(kam_eye_issues, {
                ...issueData,
                originalIssueId: issueId, // Track the original issue ID in Firestore
            });
            await remove(issueRef);
            if (isMorethanOne) {
                setCurrentIssueIndex(index);
                if (index === length) {
                    closeEditIssue();
                }
            } else {
                closeEditIssue();
            }
        } catch (error) {
            console.error('Error closing and archiving issue:', error);
        }
    }, [isMorethanOne, closeEditIssue]);

    const closeIssueOrIssues = useCallback(async (currentIssue) => {
        if (isMorethanOne && takeActionToAll) {
            try {
                setShowProgressPopup(true);

                // Iterate through each issue in the group and prepare the updates
                for (let i = 0; i < issueGroup.length; i++) {
                    const groupIssue = issueGroup[i];
                    await closeIssue(groupIssue.id, i + 1, issueGroup.length);
                }
            } catch (error) {
                console.error('Error closing and archiving issue:', error);
            } finally {
                setShowProgressPopup(false);
            }
        } else {
            closeIssue(currentIssue.id, 0, 0);
        }
    }, [isMorethanOne, takeActionToAll, issueGroup, closeIssue]);

    useEffect(() => {
        setEditedIssue(issue);
        if (takeActionToAll !== null) {
            if (confirmationAction === 'save') {
                saveEditedIssueOrIssues();
            } else if (confirmationAction === 'close') {
                closeIssueOrIssues(issue);
            }
            setTakeActionToAll(null);
            setConfirmationAction(null);
        }
    }, [takeActionToAll, confirmationAction, issue, saveEditedIssueOrIssues, closeIssueOrIssues]);

    return (
        <div className="container">
            {showConfirmationActionPopup && (
                <ConfirmationActionPopup action={confirmationAction} numberOfIssues={issueGroup.length} setTakeActionToAll={setTakeActionToAll} onShow={showConfirmationActionPopup} onClose={() => { setShowConfirmationActionPopup(false) }} />
            )}
            {showProgressPopup && (
                <ClosingProgressPopup show={showProgressPopup} currentIssue={currentIssueIndex} totalIssues={issueGroup.length} action="closing" />
            )}
            <div className="text-start mb-3">
                <div className="d-flex align-items-center">
                    <button
                    type="button"
                    className="btn btn-link ps-0 pe-2 mb-3"
                    onClick={() => { closeEditIssue() }}
                    >
                    <ArrowLeft style={{ width: '19px', height: '16px' }} />
                    </button>
                    <h5><strong>Unit {issue.Installation.Unit_ID} Issue | {issue.Installation.Center}</strong></h5>
                </div>
            </div>
            <div className="row">
                <div className="col-4 pt-1">
                    <table className="table main-background">
                        <tbody>
                            <tr className="text-start">
                                <td><strong className="text-muted">{isMorethanOne ? "FIRST DATE" : "DATE"}</strong></td>
                                <td className="text-end text-muted">{formatTimestamp(issue.createdAt).split(' ')[0]}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">TIME</strong></td>
                                <td className="text-end text-muted">{formatTimestamp(issue.createdAt).split(' ')[1]}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">ORGANIZATION</strong></td>
                                <td className="text-end text-muted">{issue.Installation.Organization}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">CENTER</strong></td>
                                <td className="text-end text-muted">{issue.Installation.Center}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">DEVICE</strong></td>
                                <td className="text-end text-muted">{issue.Installation.Dev_type}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">ISSUE ORIGIN</strong></td>
                                <td className="text-end text-muted">{issue.Issue.ID[0] === "M" ? "Manual" : "Automatic"}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">ISSUE ID</strong></td>
                                <td className="text-end text-muted">{issue.Issue.ID}</td>
                            </tr>
                            <tr className="text-start">
                                <td><strong className="text-muted">ISSUE</strong></td>
                                <td className="text-end text-muted" style={{ wordBreak: "break-word" }}>{issue.Issue.definition}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="col-4 pt-1">
                <Form.Group className="text-start">
                    <Form.Label><strong>ACTIVITY</strong></Form.Label>
                    <table className="table main-background">
                        <tbody>
                            {
                                // Extract all entries with their timestamps into a single array.
                                // createdAt (set by Osiris) is synthesized as its own row here
                                // since it no longer lives inside `timestamps`; `timestamps`
                                // itself may be entirely absent on a fresh issue.
                                (issue.createdAt ? [{
                                    timestamp: issue.createdAt,
                                    content: (
                                        <tr className="last-element" key="createdAt">
                                            <td>Created_at</td>
                                            <td>{formatTimestamp(issue.createdAt)}</td>
                                        </tr>
                                    )
                                }] : [])
                                    .concat(Object.entries(issue.timestamps || {})
                                    .flatMap(([key, value]) =>
                                        key === "messages"
                                            ? Object.entries(value).map(([messageKey, messageValue]) => ({
                                                timestamp: messageValue.timestamp,
                                                content: (
                                                    <React.Fragment key={messageKey}>
                                                        <tr>
                                                            <td>{messageKey}</td>
                                                            <td>{formatTimestamp(messageValue.timestamp)}</td>
                                                        </tr>
                                                        <tr className="last-element">
                                                            <td colSpan="2" className="message-cell text-muted" style={{ maxWidth: '200px', wordBreak: 'break-word', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                                {messageValue.message}
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                )
                                            }))
                                            : Object.entries(value).map(([subKey, timestamp]) => ({
                                                timestamp: timestamp,
                                                content: (
                                                    <tr className="last-element" key={subKey}>
                                                        <td>{subKey}</td>
                                                        <td>{formatTimestamp(timestamp)}</td>
                                                    </tr>
                                                )
                                            }))
                                    ))
                                    // Sort the array by timestamps in ascending order
                                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                                    // Render the sorted array
                                    .map(entry => entry.content)
                            }
                        </tbody>
                    </table>
                        <Form.Group className="text-start mt-2" controlId="editIssueMessage">
                            <Form.Control
                                as="textarea"
                                rows="3"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                            />
                        </Form.Group>
                    </Form.Group>
                </div>
            </div>
            <div className="row">
            <div className="col d-flex justify-content-center align-items-center flex-column">
                <Button
                    variant="primary"
                    className=""
                    onClick={() => {
                        if (isMorethanOne) {
                            setConfirmationAction('save');
                            setShowConfirmationActionPopup(true);
                        } else {
                            saveEditedIssueOrIssues();
                        }
                    }}
                >
                    <strong>{isMorethanOne ? "SAVE ISSUE GROUP" : "SAVE"}</strong>
                </Button>
                <button
                    type="button"
                    className="btn btn-link text-danger"
                    onClick={() => {
                        if (isMorethanOne) {
                            setConfirmationAction('close');
                            setShowConfirmationActionPopup(true);
                        } else {
                            closeIssueOrIssues(issue);
                        }
                    }}
                >
                    {isMorethanOne ? "Close issue group" : "Close issue"}
                </button>
            </div>
        </div>
    </div>
    );
};